import { useCallback, useEffect, useState } from 'react';
import useLatestRequest from './useLatestRequest';
import { fetchCatalog } from '../api/checkout';
import { useUnifiedAuth } from '../contexts/UnifiedAuthContext';

const defaultState = {
  products: [],
  recommendedBundles: [],
};

const useProductCatalog = () => {
  const { user, isAuthenticated, getApiToken } = useUnifiedAuth();
  const userId = user?.id;
  const { begin, cancel } = useLatestRequest();
  const [catalog, setCatalog] = useState(defaultState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCatalog = useCallback(async () => {
    const signal = begin();
    setLoading(true);
    setCatalog(defaultState);
    setError(null);
    try {
      const token = isAuthenticated ? await getApiToken() : null;
      if (signal.aborted) return;
      if (isAuthenticated && !token)
        throw new Error('We could not verify your account pricing. Please try again.');
      const data = await fetchCatalog(token || undefined, undefined, signal);
      if (signal.aborted) return;
      setCatalog(data || defaultState);
    } catch (catalogError) {
      if (signal.aborted) return;
      console.error('[useProductCatalog] Failed to load catalog', catalogError);
      setError(catalogError);
      setCatalog(defaultState);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [getApiToken, isAuthenticated, begin]);

  useEffect(() => {
    void loadCatalog();
    return cancel;
  }, [loadCatalog, cancel, userId]);

  return {
    catalog,
    loading,
    error,
    refresh: loadCatalog,
  };
};

export default useProductCatalog;
